<template>
  <div class="relative">
    <PlanOverlay :visible="projectsPerm == 'none'" :reason="projectsPerm == 'none' && isSubUser ? 'permission' : 'plan'" />
    <div class="p-4">
      <div class="flex justify-between items-center mb-4">
        <h1 class="text-2xl font-bold">{{ clientId ? "Projets du client" : "Vos projets" }}</h1>
        <button class="callToAction !px-4" @click="addNewProject" v-if="projectsPerm == 'modification'">
          Ajouter un projet
        </button>
      </div>

      <!-- Barre de recherche -->
      <div class="relative mb-4">
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Rechercher un projet..." 
          class="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-blue-300"
        />
        <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
          <i class="fas fa-search"></i>
        </span>
        <span 
          v-if="searchQuery.trim().length" 
          class="cursor-pointer absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
          @click="searchQuery = ''"
        >
          <i class="fas fa-times"></i>
        </span>
      </div>

      <!-- Filtres select et bouton de réinitialisation -->
      <div class="flex gap-2 mb-6 h-9 items-center">
        <span>Trier par :</span>
        <select class="basicInput w-fit" value="added_increasing" v-model="selectedSort">
          <option value="name_az">Nom (A → Z)</option>
          <option value="name_za">Nom (Z → A)</option>
          <option value="added_increasing">Date d'ajout (Croissant)</option>
          <option value="added_decreasing">Date d'ajout (Décroissant)</option>
          <option value="start_increasing">Date de début (Croissant)</option>
          <option value="start_decreasing">Date de début (Décroissant)</option>
          <option value="end_increasing">Date de fin (Croissant)</option>
          <option value="end_decreasing">Date de fin (Décroissant)</option>
        </select>
        <select v-model="selectedClient" class="border border-gray-300 rounded-lg p-2" v-if="!clientId">
          <option value="">Tous les clients</option>
          <option v-for="client in clients" :key="client.id" :value="client.id">{{ client.contacts[0].lastName + " " + client.contacts[0].firstName }}</option>
        </select>
        <select v-model="selectedType" class="border border-gray-300 rounded-lg p-2">
          <option value="">Tous les types de projet</option>
          <option value="__none__">Projets sans type</option>
          <template v-for="type in projectTypes"
            :key="type"
          >
            <option
              :value="type"
              v-if="type"
            >
              {{ type }}
            </option>
          </template>
        </select>
      </div>

      <template v-for="(group, index) in filteredProjects" :key="index">
        <div class="flex gap-3 items-center mb-2">
          <span class="font-bold whitespace-nowrap">{{ group.name }}</span>
          <div class="w-full border h-[1px] border-gray-300"></div>
        </div>
        <div class="basicInput mb-4 flex justify-between items-center !py-6"
          v-for="project in group.projects"
          :key="project.id"
          @click="openProjectDetails(project.id)"
        >
          <div class="w-full flex items-center">
            <!-- Trois petits points -->
            <div class="relative ">
              <button
                ref="menuButton"
                class="text-gray-500 hover:text-gray-700 !bg-transparent flex items-center justify-center !p-2 cursor-pointer"
                @click.stop="toggleMenu(project.id)"
                v-if="projectsPerm == 'modification'"
              >
                ⋮
              </button>
              <!-- Menu contextuel -->
              <div
                v-if="openMenuProjectId === project.id"
                ref="contextMenu"
                class="absolute left-0 w-40 basicInput !h-fit bg-white shadow-lg z-10"
              >
                <button
                  v-if="duplicatingProjectId !== project.id"
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 !m-0 !mb-2 !bg-transparent cursor-pointer hover:!bg-gray-100"
                  @click.stop="duplicateProject(project)"
                >
                  Dupliquer
                </button>
                <div v-else class="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 !m-0 !mb-2">
                  <span class="loader"></span>
                  Duplication...
                </div>
                <button
                  class="w-full text-left px-4 py-2 text-sm !text-red-600 hover:bg-gray-100 !m-0 !bg-transparent cursor-pointer hover:!bg-gray-100"
                  @click.stop="deleteProject(project.id)"
                >
                  Supprimer
                </button>
              </div>
            </div>
            <p class="w-full text-left ml-2">{{ project.name }}</p>
          </div>
          <div class="w-full">
            <!-- <p class="w-full text-left">Du {{ formatDate(project.startDate) }} au {{ formatDate(project.endDate) }}</p> -->
          </div>
          <div class="flex gap-4">
            <button class="callToAction w-9">▶</button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { fetchProjects, addProject, deleteProject, updateProject } from "@/services/projectService";
import { fetchClients } from "@/services/clientService";

import { fetchEventsByProjectId, addEvent } from "@/services/eventService";
import { fetchPaymentsByProjectId, addPayment } from "@/services/paymentService";

import { inject } from "vue";

import PlanOverlay from "@/components/Others/PlanOverlay.vue";

export default {
  name: "ShowProjects",
  components: {
    PlanOverlay
  },
  props: {
    clientId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      openMenuProjectId: null,
      searchQuery: "",
      selectedSort: "name_az",
      selectedClient: "",
      selectedType: "",
      duplicatingProjectId: null, // <--- Ajoute ceci
    };
  },
  setup() {
    const currentUser = inject('currentUser');
    const perm = inject('userPerm');
    const projects = inject('projects');
    const clients = inject('clients');
    const events = inject('events');
    const ressources = inject('ressources');
    return { currentUser, perm, projects, clients, events, ressources };
  },
  computed: {
    projectsPerm() {
      return this.perm().projects;
    },
    isSubUser() {
      const user = this.currentUser && this.currentUser();
      return user ? !!user.mainUserId : false;
    },
    filteredProjects() {
      // Ajoutez "Sans statut" à l'endroit voulu dans l'ordre
      const statusOrder = [
        "Devis à faire",
        "A programmer",
        "En cours",
        "A attribuer",
        "Sans statut", 
        "En pause",
        "En attente",
        "Terminé",
        "Annulé",
      ];

      let result = this.projects;

      // Filtrage consultation_solo : ne garder que les projets où l'utilisateur a au moins un event avec une ressource liée à lui
      if (this.projectsPerm === 'consultation_solo') {
        const user = this.currentUser && this.currentUser();
        console.log("Current user for consultation_solo:", user);
        if (user) {
          // On garde les projets où il existe au moins un event du projet
          // qui a une ressource dont le connectUserId === user.id
          result = result.filter(project => {
            // Cherche tous les events liés à ce projet
            const projectEvents = this.events.filter(ev => ev.projectId === project.id);
            console.log("Project events for project:", project.id, "->", projectEvents);
            // Pour chaque event, regarde si une ressource correspond à l'utilisateur
            return projectEvents.some(ev =>
              console.log("Checking events for project:", project.id, "with user:", user.id) ||
              (ev.ressources || []).some(r => {
                // r.ressourceId est l'id de la ressource dans la table ressources
                const ressource = this.ressources.find(res => res.id === r.ressourceId);
                console.log("Checking ressource:", ressource, "for user:", user.id);
                return ressource && ressource.connectUserId === user.id;
              })
            );
          });
        } else {
          result = [];
        }
      }
      console.log("Filtered projects:", result);

      // Filtre par recherche (full name)
      if (this.searchQuery.trim()) {
        const search = this.searchQuery.toLowerCase();
        result = result.filter(proj =>
          (`${proj.name || ''}`).toLowerCase().includes(search)
        );
      }

      // Tri principal (sera appliqué dans chaque groupe)
      const sortFn = (() => {
        switch(this.selectedSort){
          case 'added_increasing':
            return (a, b) => a.order - b.order;
          case 'added_decreasing':
            return (a, b) => b.order - a.order;
          case 'start_increasing':
            return (a, b) => new Date(a.startDate) - new Date(b.startDate);
          case 'start_decreasing':
            return (a, b) => new Date(b.startDate) - new Date(a.startDate);
          case 'end_increasing':
            return (a, b) => new Date(a.endDate) - new Date(b.endDate);
          case 'end_decreasing':
            return (a, b) => new Date(b.endDate) - new Date(a.endDate);
          case 'name_az':
            return (a, b) => (a.name || '').localeCompare(b.name || '');
          case 'name_za':
            return (a, b) => (b.name || '').localeCompare(a.name || '');
          default:
            return () => 0;
        }
      })();

      if (this.selectedClient) {
        result = result.filter(proj => proj.clientId === this.selectedClient);
      }

      if (this.selectedType) {
        if (this.selectedType === "__none__") {
          result = result.filter(proj => !proj.type);
        } else {
          result = result.filter(proj => proj.type === this.selectedType);
        }
      }

      if(this.clientId){
        result = result.filter(proj => proj.clientId === this.clientId);
      }

      // Grouper selon l'ordre des statuts, y compris "Sans statut"
      const grouped = statusOrder.map(status => {
        let projects;
        if (status === "Sans statut") {
          projects = result.filter(proj => !proj.status);
        } else {
          projects = result.filter(proj => proj.status === status);
        }
        return {
          name: status,
          projects: projects.slice().sort(sortFn)
        };
      }).filter(group => group.projects.length > 0);

      return grouped;
    },
    projectTypes() {
      // Récupère tous les types, y compris undefined ou ''
      const types = this.projects.map(proj => proj.type || '');
      // Supprime les doublons
      return [...new Set(types)];
    },
  },
  methods: {
    getClientName(clientId) {
      const client = this.clients.find(client => client.id === clientId);
      return client ? " pour " + client.name : '';
    },
    async addNewProject() {
      const project = await addProject();
      if(!project) return;
      project.clientId = this.clientId || null;
      console.log("New project created:", project);
      await updateProject(project);
      this.projects.push(project);
      this.$router.push({ path: `/project/${project.id}` });
    },
    updateProject(project) {
      updateProject(project);
    },
    toggleMenu(projectId) {
      this.openMenuProjectId = this.openMenuProjectId === projectId ? null : projectId;
    },
    deleteProject(projectId) {
      const idx = this.projects.findIndex(project => project.id === projectId);
      if (idx !== -1) {
        this.projects.splice(idx, 1); // <-- Splice pour la réactivité
      }
      this.openMenuIndex = null;
      deleteProject(projectId);
    },
    async duplicateProject(project) {
      this.duplicatingProjectId = project.id;
      try {
        // 1. Crée un nouveau projet
        const newProject = await addProject();
        const newProjectData = { ...project };
        newProjectData.id = newProject.id;
        newProjectData.order = newProject.order;
        newProjectData.name = `${project.name} (Copie)`;
        newProjectData.startDate = this.formatToISODate(newProject.startDate);
        newProjectData.endDate = this.formatToISODate(newProject.endDate);
        await updateProject(newProjectData);

        // 2. Duplique les events associés
        const events = await fetchEventsByProjectId(project.id);
        for (const event of events) {
          const { id, projectId, ...eventData } = event;
          const newEvent = { ...eventData, projectId: newProject.id };
          await addEvent(newEvent);
        }

        // 3. Duplique les paiements associés
        const payments = await fetchPaymentsByProjectId(project.id);
        for (const payment of payments) {
          const { id, projectId, ...paymentData } = payment;
          const newPayment = { ...paymentData, projectId: newProject.id };
          await addPayment(newPayment);
        }

        this.projects.push(newProjectData);
      } finally {
        this.duplicatingProjectId = null;
        this.openMenuProjectId = null; // Ferme le menu à la fin
      }
    },
    openProjectDetails(projectId) {
      this.$router.push({ path: `/project/${projectId}` });
    },
    formatDate(dateString) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    },
    formatToISODate(date) {
      date = new Date(date);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Mois commence à 0, donc +1
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    handleClickOutside(event) {
      // Si aucun menu ouvert, rien à faire
      if (!this.openMenuProjectId) return;

      // Récupère tous les menus contextuels et boutons (tableaux)
      const menus = this.$refs.contextMenu;
      const buttons = this.$refs.menuButton;

      // Si un seul menu, menus n'est pas un tableau
      const menusArr = Array.isArray(menus) ? menus : [menus].filter(Boolean);
      const buttonsArr = Array.isArray(buttons) ? buttons : [buttons].filter(Boolean);

      // Si le clic est dans un menu ou un bouton, on ne ferme pas
      const clickedInMenu = menusArr.some(menu => menu && menu.contains(event.target));
      const clickedInButton = buttonsArr.some(button => button && button.contains(event.target));

      if (!clickedInMenu && !clickedInButton) {
        this.openMenuProjectId = null;
      }
    }
  },
  async mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style scoped>
.loader {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #888;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}
@keyframes spin {
  0% { transform: rotate(0deg);}
  100% { transform: rotate(360deg);}
}
</style>